/**
 * useListUrlState
 *
 * URL search-param-backed list view state (ratified 2026-07-09) — page, sort, search,
 * pageSize, and filters all persist to the URL so a list survives refresh, back/forward
 * works, and any filtered view is a shareable deep link. Defaults are never serialized
 * (clean-URL rule): `page=1`, the default sort, empty search, and default page size are
 * all omitted. Discrete changes (page/sort/pageSize/filters) push a history entry;
 * search debounces 300ms then replaces (no history spam per keystroke). Selection is
 * NEVER part of this state — it is ephemeral UI state, not view state.
 * See `.github/context/list-grid-standard-behaviours.context.md` §19.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PAGE_SIZE } from './Pager';

export interface ListUrlState {
  page: number;
  /** `{field}:{asc|desc}` — or the default when the URL doesn't override it. */
  sort: string | null;
  search: string;
  pageSize: number;
  /** Serialized column filters string (`field:op:value;…`) per §8. */
  filters: string;
}

export interface UseListUrlStateOptions {
  /** Default sort — e.g. `'name:asc'` — compared against to decide whether to omit `sort` from the URL. */
  defaultSort?: string;
  defaultPageSize?: number;
}

export interface UseListUrlStateResult {
  state: ListUrlState;
  /** Discrete change -> push. */
  setPage: (page: number) => void;
  /** Discrete change -> push. Also resets page to 1. */
  setSort: (sort: string | null) => void;
  /** Keystroke-level change -> debounced 300ms, then replace. Also resets page to 1 once applied. */
  setSearch: (search: string) => void;
  /** Discrete change -> push. Also resets page to 1. */
  setPageSize: (pageSize: number) => void;
  /** Discrete change -> push. Also resets page to 1. */
  setFilters: (filters: string) => void;
  /** Clears all persisted params back to a pristine URL. */
  reset: () => void;
}

export function useListUrlState(options: UseListUrlStateOptions = {}): UseListUrlStateResult {
  const defaultPageSize = options.defaultPageSize ?? PAGE_SIZE;
  const defaultSort = options.defaultSort ?? null;
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(Number(searchParams.get('page')) || 1, 1);
  const sort = searchParams.get('sort') || defaultSort;
  const urlSearch = searchParams.get('search') ?? '';
  const pageSize = Number(searchParams.get('pageSize')) || defaultPageSize;
  const filters = searchParams.get('filters') ?? '';

  // Immediate local echo of the search box so typing feels instant; the URL only
  // updates 300ms after the user stops typing (see the debounce effect below).
  const [searchInput, setSearchInput] = useState(urlSearch);
  useEffect(() => {
    setSearchInput(urlSearch);
    // Re-sync only when the URL's search value changes externally (e.g. back/forward).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearch]);

  const applyParams = useCallback(
    (updates: Record<string, string | null>, mode: 'push' | 'replace') => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === null || value === '') {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          }
          return next;
        },
        { replace: mode === 'replace' }
      );
    },
    [setSearchParams]
  );

  const setPage = useCallback(
    (next: number) => {
      applyParams({ page: next > 1 ? String(next) : null }, 'push');
    },
    [applyParams]
  );

  const setSort = useCallback(
    (next: string | null) => {
      const clean = next && next !== defaultSort ? next : null;
      applyParams({ sort: clean, page: null }, 'push');
    },
    [applyParams, defaultSort]
  );

  const setPageSize = useCallback(
    (next: number) => {
      applyParams({ pageSize: next !== defaultPageSize ? String(next) : null, page: null }, 'push');
    },
    [applyParams, defaultPageSize]
  );

  const setFilters = useCallback(
    (next: string) => {
      applyParams({ filters: next || null, page: null }, 'push');
    },
    [applyParams]
  );

  const setSearch = useCallback((next: string) => {
    setSearchInput(next);
  }, []);

  // Debounce: 300ms after the last keystroke, replace (not push) into the URL and reset page 1.
  useEffect(() => {
    if (searchInput === urlSearch) return;
    const timer = setTimeout(() => {
      applyParams({ search: searchInput || null, page: null }, 'replace');
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, urlSearch, applyParams]);

  const reset = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
    setSearchInput('');
  }, [setSearchParams]);

  return {
    state: { page, sort, search: searchInput, pageSize, filters },
    setPage,
    setSort,
    setSearch,
    setPageSize,
    setFilters,
    reset,
  };
}
